import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { profile, heroTexture, currentlyBuilding } from "../../data/portfolio";

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="section-hero"
      className="relative min-h-[100svh] w-full flex items-end pt-28 pb-16 overflow-hidden grain"
    >
      {/* Background texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.10] mix-blend-screen"
        style={{
          backgroundImage: `url(${heroTexture})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(80% 60% at 10% 10%, rgba(255,255,255,0.04), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        {/* Top meta line */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate="visible"
          custom={0}
          className="flex items-center justify-between mb-12 md:mb-20"
        >
          <div className="flex items-center gap-3">
            <span className="inline-block w-1.5 h-1.5 bg-neutral-100 animate-pulse" />
            <span className="text-mono-label">Available · 2026</span>
          </div>
          <span className="text-mono-label hidden sm:inline">
            {profile.location}
          </span>
        </motion.div>

        {/* Positioning overline */}
        <motion.p
          variants={fade}
          initial="hidden"
          animate="visible"
          custom={1}
          className="text-mono-label mb-6"
          data-testid="hero-role-label"
        >
          {profile.role} — Web · Cloud · Native Apps
        </motion.p>

        {/* Name */}
        <motion.h1
          variants={fade}
          initial="hidden"
          animate="visible"
          custom={2}
          data-testid="hero-name"
          className="font-serif text-[16vw] sm:text-[12vw] md:text-[9vw] lg:text-[8.5rem] leading-[0.92] tracking-tighter text-neutral-100"
        >
          {profile.name}.
        </motion.h1>

        {/* Positioning statement */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate="visible"
          custom={3}
          className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end"
        >
          <p className="md:col-span-7 font-serif italic text-2xl md:text-3xl leading-[1.2] text-neutral-200 max-w-2xl">
            {profile.positioning}
          </p>

          <div className="md:col-span-5 md:flex md:justify-end">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href="#work"
                data-testid="hero-cta-projects"
                className="group inline-flex items-center gap-3 bg-neutral-100 text-neutral-950 px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-white active:scale-[0.98] transition-all"
              >
                View Projects
                <ArrowDownRight
                  size={16}
                  className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform"
                />
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 border border-neutral-800 hover:border-neutral-100 px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] text-neutral-100 transition-colors"
              >
                Say hello
              </a>
            </div>
          </div>
        </motion.div>

        {/* Footer meta */}
        <motion.div
          variants={fade}
          initial="hidden"
          animate="visible"
          custom={5}
          className="mt-16 md:mt-24 pt-8 border-t border-neutral-900 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            ["Focus", "Systems Design & Web Platforms"],
            ["Currently", "Freelance · Research"],
            ["Stack", "Laravel · React · Docker · Minio"],
            ["Time zone", "GMT+7"],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="text-mono-label mb-2">{k}</div>
              <div className="font-mono text-sm text-neutral-200">{v}</div>
            </div>
          ))}
        </motion.div>

        {/* Currently Building strip */}
        {currentlyBuilding?.length > 0 && (
          <motion.div
            variants={fade}
            initial="hidden"
            animate="visible"
            custom={6}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <span className="text-mono-label">Building now</span>
            <span className="text-neutral-800 font-mono text-xs">—</span>
            {currentlyBuilding.map((item) => (
              <span
                key={item.label}
                title={item.description}
                className="inline-flex items-center gap-2 border border-neutral-800 bg-neutral-950 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400 hover:border-neutral-600 hover:text-neutral-200 transition-colors duration-300"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {item.label}
              </span>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Hero;
